import React, { useEffect, useState } from 'react';
import {
  BarChart3,
  Bot,
  Check,
  ChevronDown,
  ChevronUp,
  Cloud,
  CloudOff,
  Clock,
  Eye,
  FileText,
  HardDrive,
  Home,
  Info,
  Loader2,
  Menu,
  MessageCircleQuestion,
  Moon,
  Calendar,
  ScrollText,
  Settings,
  Sun,
  Users,
  X,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import PromptManager from './PromptManager';
import QuestionExplorerManager from './QuestionExplorerManager';
import StorageCleanupManager from './StorageCleanupManager';
import MobileMenu from './MobileMenu';
import ProfileOrbMenu from './ProfileOrbMenu';
import SessionSettingsModal from './SessionSettingsModal';
import { useShareLink } from '../contexts/ShareLinkContext';
import { useDevicePerformance } from '../hooks/useDevicePerformance';
import { type EpisodeFamilyDetail, type YouTubeTitleSyncState } from '../services/sessionService';
import type { VideoStats, RetentionData } from '../services/youtubeAnalyticsService';
import { SessionVideoSourceStatus, type SpeakerLabels, type SpeakerSegment } from '../types';
import type { TabId } from './TabLayout';

export type VideoSourceType = 'youtube' | 'dropbox' | 'file' | null;

interface AppHeaderProps {
  isDarkMode: boolean;
  toggleDarkMode: () => void;
  userName?: string;
  userEmail?: string;
  userPicture?: string | null;
  onLogout: () => void;
  onHome: () => void;
  sessionId?: string | null;
  sessionTitle?: string;
  sessionShowSlug?: string | null;
  sessionShowTitle?: string | null;
  sessionFamily?: EpisodeFamilyDetail | null;
  token?: string;
  onTitleUpdate?: (newTitle: string) => void;
  onSessionRefresh?: () => Promise<void> | void;
  activeTabId?: string;
  onTabChange?: (tabId: TabId) => void;
  availableTabIds?: TabId[];
  selectedTtmlName?: string;
  youtubeUrl?: string | null;
  youtubeTitle?: string | null;
  youtubeTitleUpdatedAt?: string | null;
  youtubeTitleSync?: YouTubeTitleSyncState | null;
  onYoutubeUrlChange?: (url: string | null) => void;
  videoUrl?: string | null;
  videoSource?: VideoSourceType;
  onVideoUrlChange?: (url: string | null, source: VideoSourceType) => void;
  onRefreshVideoSource?: () => Promise<void> | void;
  onToggleYoutubePlayer?: () => void;
  isYoutubePlayerVisible?: boolean;
  videoStats?: VideoStats | null;
  videoDurationSeconds?: number | null;
  fetchedAt?: string | null;
  onRetentionDataUpdate?: (data: RetentionData) => void;
  speakerIds?: number[];
  speakerSegments?: Map<number, SpeakerSegment[]>;
  speakerLabels?: SpeakerLabels;
  onSaveSpeakerLabels?: (labels: SpeakerLabels) => Promise<void>;
  transcriptionChecksum?: string;
  isYouTubeMode?: boolean;
  dropboxVideoUrl?: string | null;
  videoFilename?: string | null;
  dropboxDrpUrl?: string | null;
  timelineName?: string | null;
  localVideoPath?: string | null;
  productionNotes?: string | null;
  videoFileHash?: string | null;
  videoFileSize?: number | null;
  videoFrameRate?: number | null;
  videoDuration?: number | null;
  audioDuration?: number | null;
  videoSourceStatus?: SessionVideoSourceStatus | null;
  customThumbnailUrl?: string | null;
  customThumbnailTimeSeconds?: number | null;
  onProductionMetadataUpdate?: (data: {
    dropboxVideoUrl?: string | null;
    videoFilename?: string | null;
    dropboxDrpUrl?: string | null;
    timelineName?: string | null;
    localVideoPath?: string | null;
    productionNotes?: string | null;
  }) => void;
  onVideoSourceStatusUpdate?: (status: SessionVideoSourceStatus) => void;
  publishedToHomepage?: boolean;
  onPublishChange?: (published: boolean) => void;
  isOfflineAvailable?: boolean;
  isOfflineMode?: boolean;
  isOfflineActionPending?: boolean;
  offlineActionStatusLabel?: string;
  onToggleOfflineAvailability?: () => Promise<void> | void;
  onCopyOfflineDiagnostics?: () => Promise<void> | void;
  onNavigateToFamilyMember?: (sessionId: string) => void;
}

const AppHeader: React.FC<AppHeaderProps> = ({
  isDarkMode,
  toggleDarkMode,
  userName,
  userEmail,
  userPicture,
  onLogout,
  onHome,
  sessionId,
  sessionTitle,
  sessionShowSlug,
  sessionShowTitle,
  sessionFamily,
  token,
  onTitleUpdate,
  onSessionRefresh,
  activeTabId,
  onTabChange,
  availableTabIds,
  selectedTtmlName,
  youtubeUrl,
  youtubeTitle,
  youtubeTitleUpdatedAt,
  youtubeTitleSync,
  onYoutubeUrlChange,
  videoUrl,
  videoSource,
  onVideoUrlChange,
  onRefreshVideoSource,
  onToggleYoutubePlayer,
  isYoutubePlayerVisible,
  videoStats,
  videoDurationSeconds,
  fetchedAt,
  onRetentionDataUpdate,
  speakerIds,
  speakerSegments,
  speakerLabels,
  onSaveSpeakerLabels,
  transcriptionChecksum,
  isYouTubeMode,
  dropboxVideoUrl,
  videoFilename,
  dropboxDrpUrl,
  timelineName,
  localVideoPath,
  productionNotes,
  videoFileHash,
  videoFileSize,
  videoFrameRate,
  videoDuration,
  audioDuration,
  videoSourceStatus,
  customThumbnailUrl,
  customThumbnailTimeSeconds,
  onProductionMetadataUpdate,
  onVideoSourceStatusUpdate,
  publishedToHomepage,
  onPublishChange,
  isOfflineAvailable = false,
  isOfflineMode = false,
  isOfflineActionPending = false,
  offlineActionStatusLabel,
  onToggleOfflineAvailability,
  onCopyOfflineDiagnostics,
  onNavigateToFamilyMember,
}) => {
  const navigate = useNavigate();
  const { permissions: sharePermissions, metadata: shareMetadata } = useShareLink();
  const { isCompactTouchLayout } = useDevicePerformance();
  const [showPromptManager, setShowPromptManager] = useState(false);
  const [showQuestionExplorer, setShowQuestionExplorer] = useState(false);
  const [showStorageCleanup, setShowStorageCleanup] = useState(false);
  const [showAboutModal, setShowAboutModal] = useState(false);
  const [showShareInfoModal, setShowShareInfoModal] = useState(false);
  const [showSessionSettings, setShowSessionSettings] = useState(false);
  const [initialSettingsSection, setInitialSettingsSection] = useState<'title' | 'share' | 'speakers' | 'playback' | 'youtube' | 'production'>('title');
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showWorkspaceMenu, setShowWorkspaceMenu] = useState(false);
  const [isMobileHeaderCollapsed, setIsMobileHeaderCollapsed] = useState(false);
  const workspaceMenuRef = React.useRef<HTMLDivElement>(null);

  const isPublicShare = !!sharePermissions;
  const isSessionView = !!sessionId;
  const canManageSession = !!sessionId && !isPublicShare && !!token;
  const headerEyebrow = isPublicShare ? 'Shared Review' : isSessionView ? 'MLST Session' : 'MLST Workspace';
  const headerBrandTitle = isOfflineMode && isSessionView ? 'ReScript (Offline)' : 'ReScript';
  const modalSurfaceClassName = isDarkMode
    ? 'border-[#344052] bg-[#1B2432] text-[#F4F4F4]'
    : 'border-[#D6DCE4] bg-[#F4F4F4] text-[#232E3F]';
  const modalCardClassName = isDarkMode
    ? 'border-[#344052] bg-[#101925]/[0.82] text-[#D7DDE5]'
    : 'border-[#D6DCE4] bg-white/80 text-[#566377]';
  const modalMutedTextClassName = isDarkMode ? 'text-[#C6CDD8]' : 'text-[#566377]';
  const headerSurfaceClassName = isDarkMode
    ? 'border-[#FFB52B]/20 bg-[#232E3F]/[0.96] text-[#F4F4F4] shadow-[0_24px_40px_-28px_rgba(10,14,20,0.72)]'
    : 'border-[#D6DCE4] bg-[linear-gradient(180deg,rgba(255,255,255,0.95),rgba(244,244,244,0.96))] text-[#232E3F] shadow-[0_22px_44px_-34px_rgba(35,46,63,0.28)]';
  const textButtonClassName = isDarkMode
    ? 'inline-flex min-h-[44px] min-w-[44px] items-center justify-center gap-2 rounded-full border border-white/[0.12] bg-white/[0.06] px-2.5 py-2 font-headlines text-sm font-semibold text-[#F4F4F4] transition-all hover:border-[#FFB52B]/[0.45] hover:bg-[#FFB52B] hover:text-[#232E3F] sm:min-w-0 sm:px-4'
    : 'inline-flex min-h-[44px] min-w-[44px] items-center justify-center gap-2 rounded-full border border-[#D6DCE4] bg-white/[0.92] px-2.5 py-2 font-headlines text-sm font-semibold text-[#2A3649] transition-all hover:border-[#FFB52B]/[0.5] hover:bg-[#FFB52B] hover:text-[#232E3F] sm:min-w-0 sm:px-4';
  const sessionIconButtonClassName = isDarkMode
    ? 'inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/[0.12] bg-white/[0.06] text-[#F4F4F4] transition-all hover:border-[#FFB52B]/[0.45] hover:bg-[#FFB52B] hover:text-[#232E3F]'
    : 'inline-flex h-11 w-11 items-center justify-center rounded-full border border-[#D6DCE4] bg-white/[0.92] text-[#2A3649] transition-all hover:border-[#FFB52B]/[0.5] hover:bg-[#FFB52B] hover:text-[#232E3F]';
  const eyebrowTextClassName = isDarkMode ? 'text-[#FFB52B]/80' : 'text-[#A36A00]';
  const headerTitleClassName = isDarkMode ? 'text-[#F4F4F4]' : 'text-[#232E3F]';
  const sessionTitleClassName = isDarkMode ? 'text-[#F4F4F4]' : 'text-[#232E3F]';
  const sessionMetaTextClassName = isDarkMode ? 'text-[#D5D9E0]' : 'text-[#5B687C]';
  const sessionDividerClassName = isDarkMode ? 'border-white/10' : 'border-[#D6DCE4]';
  const metadataPillClassName = isDarkMode
    ? 'border border-[#FFB52B]/20 bg-[#FFB52B]/[0.12] text-[#FFE3A1]'
    : 'border border-[#FFB52B]/35 bg-[#FFF5DE] text-[#8A6100]';
  const familySelectClassName = isDarkMode
    ? 'rounded-full border border-white/[0.12] bg-white/[0.08] px-3 py-1.5 text-xs font-semibold text-[#F4F4F4]'
    : 'rounded-full border border-[#D6DCE4] bg-white/[0.92] px-3 py-1.5 text-xs font-semibold text-[#2A3649]';
  const showCollapsedMobileHeader = isSessionView && isCompactTouchLayout && isMobileHeaderCollapsed;

  useEffect(() => {
    if (!isCompactTouchLayout) {
      setIsMobileHeaderCollapsed(false);
      return;
    }

    if (isSessionView) {
      setIsMobileHeaderCollapsed(true);
    }
  }, [isCompactTouchLayout, isSessionView, sessionId]);

  useEffect(() => {
    if (typeof window === 'undefined' || !canManageSession) return;
    const params = new URLSearchParams(window.location.search);
    const requestedSection = params.get('session_settings');
    if (!requestedSection) return;
    if (['title', 'share', 'speakers', 'playback', 'youtube', 'production'].includes(requestedSection)) {
      setInitialSettingsSection(requestedSection as 'title' | 'share' | 'speakers' | 'playback' | 'youtube' | 'production');
      setShowSessionSettings(true);
    }
  }, [canManageSession, sessionId]);

  useEffect(() => {
    if (!showWorkspaceMenu) return undefined;

    const handlePointerDown = (event: MouseEvent) => {
      if (!workspaceMenuRef.current?.contains(event.target as Node)) {
        setShowWorkspaceMenu(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowWorkspaceMenu(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [showWorkspaceMenu]);

  const showSessionShow = !isPublicShare
    && !!sessionShowTitle
    && sessionShowSlug !== 'mlst'
    && sessionShowTitle !== 'Machine Learning Street Talk';
  const showSessionFamily = !isPublicShare && !!sessionFamily;

  const renderDesktopControls = () => {
    if (isPublicShare) {
      return (
        <>
          <button type="button" onClick={onHome} className={textButtonClassName} title="All videos">
            <Home className="h-4 w-4" />
            <span className="hidden sm:inline">All Videos</span>
          </button>
          <button
            type="button"
            onClick={() => setShowShareInfoModal(true)}
            className={textButtonClassName}
            title="Share information and About ReScript"
          >
            <Info className="h-4 w-4" />
            <span className="hidden sm:inline">Share Info</span>
          </button>
          <button
            type="button"
            onClick={toggleDarkMode}
            className={textButtonClassName}
            title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {isDarkMode ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
        </>
      );
    }

    return (
      <>
        {!isSessionView && (
          <>
            <button
              type="button"
              onClick={() => setShowQuestionExplorer(true)}
              className={textButtonClassName}
              title="Browse questions"
            >
              <MessageCircleQuestion className="h-4 w-4" />
              <span className="hidden sm:inline">Questions</span>
            </button>
            <button
              type="button"
              onClick={() => setShowStorageCleanup(true)}
              className={textButtonClassName}
              title="Storage cleanup"
            >
              <HardDrive className="h-4 w-4" />
              <span className="hidden sm:inline">Cleanup</span>
            </button>
          </>
        )}
        <button
          type="button"
          onClick={() => navigate('/agents')}
          className={textButtonClassName}
          title="Agent Sessions"
        >
          <Bot className="h-4 w-4" />
          <span className="hidden sm:inline">Agents</span>
        </button>
        <button
          type="button"
          onClick={() => navigate('/schedules')}
          className={textButtonClassName}
          title="Agent Schedules"
        >
          <Calendar className="h-4 w-4" />
          <span className="hidden sm:inline">Schedules</span>
        </button>
        <button
          type="button"
          onClick={() => setShowPromptManager(true)}
          className={textButtonClassName}
          title="Prompt Manager"
        >
          <ScrollText className="h-4 w-4" />
          <span className="hidden sm:inline">Prompts</span>
        </button>
        <button
          type="button"
          onClick={() => navigate('/settings')}
          className={textButtonClassName}
          title="Settings"
        >
          <Settings className="h-4 w-4" />
          <span className="hidden sm:inline">Settings</span>
        </button>
        <button
          type="button"
          onClick={toggleDarkMode}
          className={textButtonClassName}
          title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {isDarkMode ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </button>
        <button
          type="button"
          onClick={() => setShowAboutModal(true)}
          className={textButtonClassName}
          title="About ReScript"
        >
          <Info className="h-4 w-4" />
        </button>
        <ProfileOrbMenu
          isDarkMode={isDarkMode}
          name={userName}
          email={userEmail}
          picture={userPicture}
          onLogout={onLogout}
        />
      </>
    );
  };

  const renderSessionDesktopControls = () => (
    <div className="flex items-center gap-2 whitespace-nowrap">
      {onToggleOfflineAvailability && (
        <button
          type="button"
          onClick={() => {
            void onToggleOfflineAvailability();
          }}
          disabled={isOfflineActionPending}
          className={`${textButtonClassName} ${isOfflineActionPending ? 'cursor-wait opacity-70' : ''}`}
          title={isOfflineAvailable ? 'Remove offline copy' : 'Make available offline'}
        >
          {isOfflineActionPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : isOfflineAvailable ? (
            <CloudOff className="h-4 w-4" />
          ) : (
            <Cloud className="h-4 w-4" />
          )}
          <span className="hidden sm:inline">
            {isOfflineActionPending
              ? 'Updating'
              : isOfflineAvailable
                ? 'Offline Saved'
                : 'Save Offline'}
          </span>
        </button>
      )}
      <button
        type="button"
        onClick={() => (isPublicShare ? setShowShareInfoModal(true) : setShowAboutModal(true))}
        className={sessionIconButtonClassName}
        title={isPublicShare ? 'Share information and About ReScript' : 'About ReScript'}
      >
        <Info className="h-5 w-5" />
      </button>
      {canManageSession && (
        <button
          type="button"
          onClick={() => {
            setInitialSettingsSection('title');
            setShowSessionSettings(true);
          }}
          className={sessionIconButtonClassName}
          title="Session settings"
        >
          <Settings className="h-5 w-5" />
        </button>
      )}
      <button
        type="button"
        onClick={toggleDarkMode}
        className={sessionIconButtonClassName}
        title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
      >
        {isDarkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
      </button>
      {!isPublicShare && (
        <ProfileOrbMenu
          isDarkMode={isDarkMode}
          name={userName}
          email={userEmail}
          picture={userPicture}
          onLogout={onLogout}
        />
      )}
    </div>
  );

  const renderSessionMobileControls = () => (
    <div className="flex items-center gap-2 whitespace-nowrap">
      <button
        type="button"
        onClick={() => setShowMobileMenu(true)}
        className={sessionIconButtonClassName}
        title="Open session menu"
      >
        <Menu className="h-5 w-5" />
      </button>
      <button
        type="button"
        onClick={() => (isPublicShare ? setShowShareInfoModal(true) : setShowAboutModal(true))}
        className={sessionIconButtonClassName}
        title={isPublicShare ? 'Share information' : 'About ReScript'}
      >
        <Info className="h-5 w-5" />
      </button>
      <button
        type="button"
        onClick={toggleDarkMode}
        className={sessionIconButtonClassName}
        title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
      >
        {isDarkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
      </button>
      {!isPublicShare && (
        <ProfileOrbMenu
          isDarkMode={isDarkMode}
          name={userName}
          email={userEmail}
          picture={userPicture}
          onLogout={onLogout}
        />
      )}
    </div>
  );

  return (
    <>
      <header className={`relative z-50 flex-shrink-0 border-b backdrop-blur-xl ${headerSurfaceClassName}`}>
        {showCollapsedMobileHeader ? (
          <div className="px-4 py-2">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className={`font-headlines text-[10px] font-semibold uppercase tracking-[0.28em] ${eyebrowTextClassName}`}>
                  {headerEyebrow}
                </p>
                <span className={`block truncate font-headlines text-sm font-bold ${sessionTitleClassName}`}>
                  {sessionTitle || 'Session'}
                </span>
              </div>
              <button
                type="button"
                onClick={() => setIsMobileHeaderCollapsed(false)}
                className={sessionIconButtonClassName}
                title="Expand header"
              >
                <ChevronDown className="h-5 w-5" />
              </button>
            </div>
          </div>
        ) : (
          <div className="px-4 py-4 md:px-6">
            <div className="flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={onHome}
                className="flex min-w-0 items-center gap-3 transition-opacity hover:opacity-85"
                title="Go to home"
              >
                <img
                  src="/logos/combination_mark_yellow.svg"
                  alt="ReScript"
                  className="h-9 w-auto md:h-11"
                />
                <div className="min-w-0 text-left">
                  <p className={`font-headlines text-[10px] font-semibold uppercase tracking-[0.28em] ${eyebrowTextClassName}`}>
                    {headerEyebrow}
                  </p>
                  <span className={`block truncate font-headlines text-lg font-bold md:text-xl ${headerTitleClassName}`}>
                    {headerBrandTitle}
                  </span>
                </div>
              </button>

              <div className="flex items-center gap-2">
                {isSessionView ? (
                  <>
                    {isCompactTouchLayout ? (
                      <div className="flex">
                        {renderSessionMobileControls()}
                      </div>
                    ) : (
                      <div className="flex">
                        {renderSessionDesktopControls()}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {!isCompactTouchLayout ? (
                      <div className="flex items-center gap-1 sm:gap-2">
                        {renderDesktopControls()}
                      </div>
                    ) : (
                      <div className="relative flex items-center gap-2" ref={workspaceMenuRef}>
                        {!isPublicShare && (
                          <button
                            type="button"
                            onClick={() => setShowWorkspaceMenu((prev) => !prev)}
                            className={sessionIconButtonClassName}
                            title={showWorkspaceMenu ? 'Close menu' : 'Open menu'}
                          >
                            {showWorkspaceMenu ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                          </button>
                        )}
                        {!isPublicShare && (
                          <ProfileOrbMenu
                            isDarkMode={isDarkMode}
                            name={userName}
                            email={userEmail}
                            picture={userPicture}
                            onLogout={onLogout}
                          />
                        )}
                        {showWorkspaceMenu && (
                          <div className={`absolute right-0 top-full z-50 mt-2 w-64 rounded-2xl border shadow-2xl ${
                            isDarkMode
                              ? 'border-gray-700 bg-gray-900 text-gray-100'
                              : 'border-gray-200 bg-white text-gray-900'
                          }`}>
                            <nav className="flex flex-col p-2 gap-1">
                              {!isSessionView && (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => { setShowQuestionExplorer(true); setShowWorkspaceMenu(false); }}
                                    className={`flex items-center gap-3 rounded-xl px-4 py-3 text-sm transition-colors ${
                                      isDarkMode ? 'hover:bg-gray-800 text-gray-200' : 'hover:bg-gray-100 text-gray-700'
                                    }`}
                                  >
                                    <MessageCircleQuestion className="h-5 w-5 flex-shrink-0" />
                                    <span>Questions</span>
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => { setShowStorageCleanup(true); setShowWorkspaceMenu(false); }}
                                    className={`flex items-center gap-3 rounded-xl px-4 py-3 text-sm transition-colors ${
                                      isDarkMode ? 'hover:bg-gray-800 text-gray-200' : 'hover:bg-gray-100 text-gray-700'
                                    }`}
                                  >
                                    <HardDrive className="h-5 w-5 flex-shrink-0" />
                                    <span>Cleanup</span>
                                  </button>
                                </>
                              )}
                              <button
                                type="button"
                                onClick={() => { navigate('/schedules'); setShowWorkspaceMenu(false); }}
                                className={`flex items-center gap-3 rounded-xl px-4 py-3 text-sm transition-colors ${
                                  isDarkMode ? 'hover:bg-gray-800 text-gray-200' : 'hover:bg-gray-100 text-gray-700'
                                }`}
                              >
                                <Calendar className="h-5 w-5 flex-shrink-0" />
                                <span>Agent Schedules</span>
                              </button>
                              <button
                                type="button"
                                onClick={() => { setShowPromptManager(true); setShowWorkspaceMenu(false); }}
                                className={`flex items-center gap-3 rounded-xl px-4 py-3 text-sm transition-colors ${
                                  isDarkMode ? 'hover:bg-gray-800 text-gray-200' : 'hover:bg-gray-100 text-gray-700'
                                }`}
                              >
                                <ScrollText className="h-5 w-5 flex-shrink-0" />
                                <span>Prompts</span>
                              </button>
                              <button
                                type="button"
                                onClick={() => { navigate('/settings'); setShowWorkspaceMenu(false); }}
                                className={`flex items-center gap-3 rounded-xl px-4 py-3 text-sm transition-colors ${
                                  isDarkMode ? 'hover:bg-gray-800 text-gray-200' : 'hover:bg-gray-100 text-gray-700'
                                }`}
                              >
                                <Settings className="h-5 w-5 flex-shrink-0" />
                                <span>Settings</span>
                              </button>
                              <div className={`my-1 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`} />
                              <button
                                type="button"
                                onClick={() => { toggleDarkMode(); setShowWorkspaceMenu(false); }}
                                className={`flex items-center gap-3 rounded-xl px-4 py-3 text-sm transition-colors ${
                                  isDarkMode ? 'hover:bg-gray-800 text-gray-200' : 'hover:bg-gray-100 text-gray-700'
                                }`}
                              >
                                {isDarkMode ? <Sun className="h-5 w-5 flex-shrink-0" /> : <Moon className="h-5 w-5 flex-shrink-0" />}
                                <span>{isDarkMode ? 'Light Mode' : 'Dark Mode'}</span>
                              </button>
                              <button
                                type="button"
                                onClick={() => { setShowAboutModal(true); setShowWorkspaceMenu(false); }}
                                className={`flex items-center gap-3 rounded-xl px-4 py-3 text-sm transition-colors ${
                                  isDarkMode ? 'hover:bg-gray-800 text-gray-200' : 'hover:bg-gray-100 text-gray-700'
                                }`}
                              >
                                <Info className="h-5 w-5 flex-shrink-0" />
                                <span>About ReScript</span>
                              </button>
                            </nav>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {isSessionView && sessionTitle && (
              <div className={`flex flex-col border-t md:flex-row md:items-end md:justify-between ${sessionDividerClassName} ${
                isCompactTouchLayout ? 'mt-2 gap-1 pt-2' : 'mt-4 gap-2 pt-4'
              }`}>
                <div className="min-w-0 flex-1">
                  <div className={`flex items-start ${isCompactTouchLayout ? 'gap-2' : 'gap-3'}`}>
                    <div className="min-w-0 flex-1">
                      <h1 className={`truncate font-headlines font-bold ${sessionTitleClassName} ${
                        isCompactTouchLayout ? 'text-[0.95rem] leading-tight' : 'text-lg md:text-[1.45rem] md:leading-snug'
                      }`}>
                        {sessionTitle}
                      </h1>
                      {selectedTtmlName && (
                        <p className={`font-body ${sessionMetaTextClassName} ${
                          isCompactTouchLayout ? 'mt-0.5 text-xs leading-tight' : 'mt-1 text-sm'
                        }`}>
                          Active metadata: {selectedTtmlName.replace(/\.ttml$/i, '')}
                        </p>
                      )}
                      {(showSessionShow || showSessionFamily) && (
                        <div className={`mt-1 flex flex-wrap items-center gap-2 ${sessionMetaTextClassName}`}>
                          {showSessionShow && (
                            <span className={`inline-flex items-center rounded-full ${metadataPillClassName} ${
                              isCompactTouchLayout ? 'px-2.5 py-1 text-[10px]' : 'px-3 py-1.5 text-[11px]'
                            }`}>
                              {sessionShowTitle}
                              {sessionShowSlug ? ` · ${sessionShowSlug}` : ''}
                            </span>
                          )}
                          {showSessionFamily && sessionFamily && (
                            <>
                              <span className={`inline-flex items-center rounded-full ${metadataPillClassName} ${
                                isCompactTouchLayout ? 'px-2.5 py-1 text-[10px]' : 'px-3 py-1.5 text-[11px]'
                              }`}>
                                {sessionFamily.title || 'Episode family'}
                              </span>
                              {sessionFamily.versionNumber != null && (
                                <span className="font-headlines text-[11px] font-semibold uppercase tracking-[0.16em]">
                                  v{sessionFamily.versionNumber} of {sessionFamily.versionCount}
                                </span>
                              )}
                              {sessionFamily.members?.length > 1 && onNavigateToFamilyMember && (
                                <select
                                  className={familySelectClassName}
                                  value={sessionId || ''}
                                  onChange={(event) => {
                                    if (event.target.value) {
                                      onNavigateToFamilyMember(event.target.value);
                                    }
                                  }}
                                >
                                  {sessionFamily.members.map((member) => (
                                    <option key={member.sessionId} value={member.sessionId}>
                                      v{member.versionNumber ?? '?'}{member.isCurrent ? ' • Current' : ''} • {member.title}
                                    </option>
                                  ))}
                                </select>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                    {isCompactTouchLayout && (
                      <button
                        type="button"
                        onClick={() => setIsMobileHeaderCollapsed(true)}
                        className={`inline-flex items-center justify-center rounded-full border transition-all ${
                          isDarkMode
                            ? 'border-white/[0.12] bg-white/[0.06] text-[#F4F4F4] hover:border-[#FFB52B]/[0.45] hover:bg-[#FFB52B] hover:text-[#232E3F]'
                            : 'border-[#D6DCE4] bg-white/[0.92] text-[#2A3649] hover:border-[#FFB52B]/[0.5] hover:bg-[#FFB52B] hover:text-[#232E3F]'
                        } h-9 w-9`}
                        title="Collapse header"
                      >
                        <ChevronUp className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
                {selectedTtmlName && (
                  <div className={`inline-flex max-w-full items-center gap-2 self-start rounded-full md:self-auto ${metadataPillClassName} ${
                    isCompactTouchLayout ? 'px-3 py-1.5 text-[11px]' : 'px-4 py-2 text-xs'
                  }`}>
                    <BarChart3 className="h-3.5 w-3.5 flex-shrink-0" />
                    <span className="font-headlines font-semibold uppercase tracking-[0.18em]">Metadata</span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </header>

      {showSessionSettings && sessionId && !isPublicShare && (
        <SessionSettingsModal
          isOpen={showSessionSettings}
          onClose={() => setShowSessionSettings(false)}
          initialSection={initialSettingsSection}
          isDarkMode={isDarkMode}
          sessionId={sessionId}
          sessionTitle={sessionTitle}
          token={token}
          onTitleUpdate={onTitleUpdate}
          onSessionRefresh={onSessionRefresh}
          youtubeUrl={youtubeUrl}
          youtubeTitle={youtubeTitle}
          youtubeTitleUpdatedAt={youtubeTitleUpdatedAt}
          youtubeTitleSync={youtubeTitleSync}
          videoUrl={videoUrl}
          videoSource={videoSource}
          onYoutubeUrlChange={onYoutubeUrlChange}
          onVideoUrlChange={onVideoUrlChange}
          onRefreshVideoSource={onRefreshVideoSource}
          videoStats={videoStats}
          videoDurationSeconds={videoDurationSeconds}
          fetchedAt={fetchedAt}
          onRetentionDataUpdate={onRetentionDataUpdate}
          speakerIds={speakerIds}
          speakerSegments={speakerSegments}
          speakerLabels={speakerLabels}
          onSaveSpeakerLabels={onSaveSpeakerLabels}
          transcriptionChecksum={transcriptionChecksum}
          isYouTubeMode={isYouTubeMode}
          dropboxVideoUrl={dropboxVideoUrl}
          videoFilename={videoFilename}
          dropboxDrpUrl={dropboxDrpUrl}
          timelineName={timelineName}
          localVideoPath={localVideoPath}
          productionNotes={productionNotes}
          videoFileHash={videoFileHash}
          videoFileSize={videoFileSize}
          videoFrameRate={videoFrameRate}
          videoDuration={videoDuration}
          audioDuration={audioDuration}
          videoSourceStatus={videoSourceStatus}
          customThumbnailUrl={customThumbnailUrl}
          customThumbnailTimeSeconds={customThumbnailTimeSeconds}
          onProductionMetadataUpdate={onProductionMetadataUpdate}
          onVideoSourceStatusUpdate={onVideoSourceStatusUpdate}
          publishedToHomepage={publishedToHomepage}
          onPublishChange={onPublishChange}
        />
      )}

      <MobileMenu
        isOpen={showMobileMenu}
        onClose={() => setShowMobileMenu(false)}
        isDarkMode={isDarkMode}
        toggleDarkMode={toggleDarkMode}
        userName={userName}
        onLogout={onLogout}
        onHome={onHome}
        sessionTitle={sessionTitle}
        sharePermissions={sharePermissions}
        shareMetadata={shareMetadata}
        activeTabId={activeTabId}
        onTabChange={onTabChange}
        availableTabIds={availableTabIds}
        selectedTtmlName={selectedTtmlName}
        hasVideoUrl={!!videoUrl}
        isYoutubePlayerVisible={isYoutubePlayerVisible}
        onToggleYoutubePlayer={onToggleYoutubePlayer}
        hasSpeakers={(speakerIds?.length || 0) > 0}
        onOpenSettings={canManageSession ? () => {
          setInitialSettingsSection('title');
          setShowSessionSettings(true);
        } : undefined}
        isOfflineAvailable={isOfflineAvailable}
        isOfflineMode={isOfflineMode}
        isOfflineActionPending={isOfflineActionPending}
        offlineActionStatusLabel={offlineActionStatusLabel}
        onToggleOfflineAvailability={onToggleOfflineAvailability}
        onCopyOfflineDiagnostics={onCopyOfflineDiagnostics}
      />

      {showShareInfoModal && sharePermissions && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#111826]/[0.72] p-4 backdrop-blur-sm" onClick={() => setShowShareInfoModal(false)}>
          <div
            className={`w-full max-w-md rounded-[28px] border p-6 shadow-[0_32px_72px_-32px_rgba(10,14,20,0.9)] ${modalSurfaceClassName}`}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between">
              <h2 className="flex items-center gap-2 font-headlines text-lg font-bold">
                <Info className="h-5 w-5 text-brand-yellow" />
                <span>Share Link Information</span>
              </h2>
              <button type="button" onClick={() => setShowShareInfoModal(false)} className="rounded-full border border-transparent p-1 transition-colors hover:border-[#FFB52B]/30 hover:bg-[#FFB52B]/10">
                <X className="h-5 w-5" />
              </button>
            </div>

            {shareMetadata?.label && (
              <div className={`mb-4 rounded-2xl border p-3 text-sm ${modalCardClassName}`}>
                <span className="font-headlines font-semibold text-[#232E3F] dark:text-[#F4F4F4]">Label:</span> {shareMetadata.label}
              </div>
            )}

            <div className="space-y-3">
              {shareMetadata?.maxViewers && (
                <div className={`flex items-start gap-3 rounded-2xl border p-3 ${modalCardClassName}`}>
                  <Users className="mt-0.5 h-5 w-5 text-brand-yellow" />
                  <div className="text-sm">
                    <div className="font-headlines font-semibold text-[#232E3F] dark:text-[#F4F4F4]">Viewer Limit</div>
                    <div>{shareMetadata.currentViewers || 0} / {shareMetadata.maxViewers} unique IPs used</div>
                  </div>
                </div>
              )}
              {shareMetadata?.maxTotalViews && (
                <div className={`flex items-start gap-3 rounded-2xl border p-3 ${modalCardClassName}`}>
                  <Eye className="mt-0.5 h-5 w-5 text-brand-yellow" />
                  <div className="text-sm">
                    <div className="font-headlines font-semibold text-[#232E3F] dark:text-[#F4F4F4]">Total Views</div>
                    <div>{shareMetadata.totalViews || 0} / {shareMetadata.maxTotalViews} views used</div>
                  </div>
                </div>
              )}
              {shareMetadata?.expiresAt && (
                <div className={`flex items-start gap-3 rounded-2xl border p-3 ${modalCardClassName}`}>
                  <Clock className="mt-0.5 h-5 w-5 text-brand-yellow" />
                  <div className="text-sm">
                    <div className="font-headlines font-semibold text-[#232E3F] dark:text-[#F4F4F4]">Expires</div>
                    <div>{new Date(shareMetadata.expiresAt).toLocaleString()}</div>
                  </div>
                </div>
              )}
            </div>

            <div className="mt-5 grid grid-cols-2 gap-2 text-sm">
              <div className={`flex items-center gap-2 rounded-2xl border p-3 ${modalCardClassName}`}>
                <FileText className="h-4 w-4 text-brand-yellow" />
                <span>Transcript</span>
                {sharePermissions.transcriptsEnabled && <Check className="ml-auto h-4 w-4 text-brand-yellow" />}
              </div>
              {sharePermissions.rankingEnabled && (
                <div className={`flex items-center gap-2 rounded-2xl border p-3 ${modalCardClassName}`}>
                  <BarChart3 className="h-4 w-4 text-brand-yellow" />
                  <span>Topic Map</span>
                  <Check className="ml-auto h-4 w-4 text-brand-yellow" />
                </div>
              )}
              {sharePermissions.questionsEnabled && (
                <div className={`flex items-center gap-2 rounded-2xl border p-3 ${modalCardClassName}`}>
                  <MessageCircleQuestion className="h-4 w-4 text-brand-yellow" />
                  <span>Questions</span>
                  <Check className="ml-auto h-4 w-4 text-brand-yellow" />
                </div>
              )}
            </div>

            <div className={`mt-5 rounded-[24px] border p-4 ${modalCardClassName}`}>
              <div className="mb-2 flex items-center gap-2">
                <img src="/logos/combination_mark_yellow.svg" alt="ReScript" className="h-7 w-auto" />
                <div>
                  <div className="font-headlines text-[10px] font-semibold uppercase tracking-[0.22em] text-[#C98A00] dark:text-[#FFB52B]/80">
                    MLST
                  </div>
                  <div className="font-headlines text-base font-bold text-[#232E3F] dark:text-[#F4F4F4]">
                    About ReScript
                  </div>
                </div>
              </div>
              <div className={`space-y-3 text-sm ${modalMutedTextClassName}`}>
                <p>
                  ReScript is MLST&apos;s content management system, review app, and editing software for podcasts, video editing, shares, search, captions, and agentic production workflows.
                </p>
                <p>
                  It combines transcript authoring, metadata generation, context-file handling, search, public shares, video-linked review, and autonomous editing workflows in one system.
                </p>
                <p>
                  For licensing or collaboration enquiries, contact{' '}
                  <a href="mailto:tim@mlst.ai" className="font-headlines font-semibold text-[#C98A00] underline decoration-[#FFB52B]/60 underline-offset-4 hover:opacity-80 dark:text-brand-yellow">
                    Tim Scarfe
                  </a>.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {showAboutModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#111826]/[0.72] p-4 backdrop-blur-sm" onClick={() => setShowAboutModal(false)}>
          <div
            className={`w-full max-w-md rounded-[28px] border p-6 shadow-[0_32px_72px_-32px_rgba(10,14,20,0.9)] ${modalSurfaceClassName}`}
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setShowAboutModal(false)}
              className="absolute right-8 top-8 rounded-full border border-transparent p-1 transition-colors hover:border-[#FFB52B]/30 hover:bg-[#FFB52B]/10"
            >
              <X className="h-5 w-5" />
            </button>
            <div className="mb-4 flex items-center gap-3">
              <img src="/logos/combination_mark_yellow.svg" alt="ReScript" className="h-10 w-auto" />
              <div>
                <p className="font-headlines text-[10px] font-semibold uppercase tracking-[0.28em] text-[#C98A00] dark:text-[#FFB52B]/80">
                  MLST
                </p>
                <h2 className="font-headlines text-xl font-bold">About ReScript</h2>
              </div>
            </div>
            <div className={`space-y-4 font-body text-sm ${modalMutedTextClassName}`}>
              <p>
                ReScript is MLST&apos;s content management system, review app, and editing software for podcasts, video editing, shares, search, captions, and agentic production workflows.
              </p>
              <p>
                It combines transcript authoring, metadata generation, context-file handling, search, public shares, video-linked review, and autonomous editing workflows in one system.
              </p>
              <p>
                For licensing or collaboration enquiries, contact{' '}
                <a href="mailto:tim@mlst.ai" className="font-headlines font-semibold text-[#C98A00] underline decoration-[#FFB52B]/60 underline-offset-4 hover:opacity-80 dark:text-brand-yellow">
                  Tim Scarfe
                </a>.
              </p>
            </div>
          </div>
        </div>
      )}

      <PromptManager
        isOpen={showPromptManager}
        onClose={() => setShowPromptManager(false)}
        token={token || ''}
        isDarkMode={isDarkMode}
      />
      <QuestionExplorerManager
        isOpen={showQuestionExplorer}
        onClose={() => setShowQuestionExplorer(false)}
        token={token || ''}
        isDarkMode={isDarkMode}
      />
      <StorageCleanupManager
        isOpen={showStorageCleanup}
        onClose={() => setShowStorageCleanup(false)}
        token={token || ''}
        isDarkMode={isDarkMode}
      />
    </>
  );
};

export default AppHeader;
